import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Truck, ShieldCheck, BadgeCheck, RefreshCw } from "lucide-react";
import api, { formatNaira } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO_SLIDES = [
  {
    eyebrow: "Adire · Heritage Collection",
    titleTop: "Wear",
    titleMid: "the",
    titleHighlight: "story",
    titleDot: ".",
    subtitle: "Hand-dyed, reimagined for the modern wardrobe.",
    image: "https://store.ajoyo.org/wp-content/uploads/2026/05/25ad8da2-cffd-41c2-89d0-1d591d402966.jpeg",
    badgeTitle: "Made with",
    badgeSub: "Heritage",
    bg: "#FDFBF7",
    accent: "#FED700",
    cta: "Shop the collection",
    ctaLink: "/shop",
  },
  {
    eyebrow: "New In · Accessories",
    titleTop: "Refined",
    titleMid: "for",
    titleHighlight: "every day",
    titleDot: ".",
    subtitle: "Premium caps, that needs no explanation.",
    image: "https://store.ajoyo.org/wp-content/uploads/2026/05/bafba2f1-fe87-4c49-a6c1-c37f9f607707.jpeg",
    badgeTitle: "Best-seller",
    badgeSub: "Premium Cap",
    bg: "#F3EFEA",
    accent: "#FED700",
    cta: "Shop Accessories",
    ctaLink: "/shop?category=Accessories",
  },
  {
    eyebrow: "Carry the Craft · Bags",
    titleTop: "Patterns",
    titleMid: "that",
    titleHighlight: "travel",
    titleDot: ".",
    subtitle: "Laptop bags and totes — engineered for the modern commute.",
    image: "https://store.ajoyo.org/wp-content/uploads/2026/02/000A6567.jpg-scaled.jpeg",
    badgeTitle: "Limited drop",
    badgeSub: "Adire Series",
    bg: "#EDE7E0",
    accent: "#FED700",
    cta: "Shop Bags",
    ctaLink: "/shop?category=Bags",
  },
];

const CATEGORY_TILES = [
  {
    name: "Accessories",
    image: "https://store.ajoyo.org/wp-content/uploads/2026/02/000A6618.jpg-scaled.jpeg",
    count: "5+ items",
    link: "/shop?category=Accessories",
  },
  {
    name: "Clothing",
    image: "https://store.ajoyo.org/wp-content/uploads/2026/02/000A6548.jpg-scaled.jpeg",
    count: "2+ items",
    link: "/shop?category=Clothing",
  },
  {
    name: "Bags",
    image: "https://store.ajoyo.org/wp-content/uploads/2026/02/000A6567.jpg-scaled.jpeg",
    count: "2+ items",
    link: "/shop?category=Bags",
  },
];

const PERKS = [
  { Icon: Truck, title: "Fast and Secure", sub: "Nationwide delivery" },
  { Icon: ShieldCheck, title: "Secure checkout", sub: "Paystack encrypted" },
  { Icon: BadgeCheck, title: "Authentic Adire", sub: "Hand-dyed in Lagos" },
  { Icon: RefreshCw, title: "Easy returns", sub: "7-day guarantee" },
];

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("featured");
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  // Auto-carousel
  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(() => {
      setSlide((s) => (s + 1) % HERO_SLIDES.length);
    }, 5500);
    return () => clearInterval(timerRef.current);
  }, [paused]);

  const featured = products.filter((p) => p.review_count > 0);
  const onSale = products.filter((p) => p.original_price && p.original_price > p.price);
  const topRated = [...products].sort((a, b) => b.rating - a.rating).slice(0, 8);
  const visible = tab === "featured" ? featured : tab === "onSale" ? (onSale.length ? onSale : products.slice(0, 4)) : topRated;

  const tabs = [
    { k: "featured", l: "Featured" },
    { k: "onSale", l: "On Sale" },
    { k: "topRated", l: "Top Rated" },
  ];

  const current = HERO_SLIDES[slide];
  const goPrev = () => setSlide((s) => (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const goNext = () => setSlide((s) => (s + 1) % HERO_SLIDES.length);

  return (
    <div data-testid="home-page">
      {/* HERO CAROUSEL */}
      <section
        className="relative overflow-hidden"
        style={{ background: current.bg, transition: "background-color 800ms ease" }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        data-testid="hero-carousel"
      >
        {/* decorative grain */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#333E48 1px, transparent 1px)",
            backgroundSize: "4px 4px",
          }}
        />

        {/* Decorative grid lines */}
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[8%] top-0 bottom-0 w-px bg-black/[0.04]" />
          <div className="absolute right-[8%] top-0 bottom-0 w-px bg-black/[0.04]" />
        </div>

        {/* Floating slide number */}
        <div aria-hidden className="hidden md:block absolute right-12 top-8 font-serif italic text-[20rem] leading-none text-[#333E48]/[0.04] select-none pointer-events-none">
          0{slide + 1}
        </div>

        {/* Side rail (left) */}
        <div className="hidden lg:flex flex-col items-center gap-4 absolute left-6 top-1/2 -translate-y-1/2 z-10">
          <span className="text-[10px] tracking-[0.35em] uppercase text-[#6A747D] [writing-mode:vertical-rl] rotate-180">
            EST. 2025 · Lagos
          </span>
          <div className="w-px h-16 bg-[#333E48]/20" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-24 md:pb-32 grid md:grid-cols-12 gap-8 md:gap-12 items-center relative">
          {/* TEXT */}
          <div className="md:col-span-7 relative z-10 min-h-[420px] md:min-h-[540px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={slide + "-text"}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-12 h-px bg-[#333E48]" />
                  <p className="label-eyebrow flex items-center gap-2" data-testid="hero-eyebrow">
                    <span className="text-[#FED700]">•</span>
                     {current.eyebrow}
                  </p>
                </div>
                <h1 className="font-serif tracking-tighter leading-[0.92] text-6xl sm:text-7xl lg:text-[8.5rem]">
                  <span className="block">{current.titleTop}</span>
                  <span className="inline">{current.titleMid} </span>
                  <span
                    className="italic relative inline-block"
                    style={{ background: `linear-gradient(transparent 62%, ${current.accent} 62%)` }}
                    data-testid="hero-highlight"
                  >
                    {current.titleHighlight}
                  </span>
                  <span>{current.titleDot}</span>
                </h1>
                <p className="mt-8 text-lg text-[#6A747D] max-w-md leading-relaxed">{current.subtitle}</p>
                <div className="mt-10 flex gap-4 items-center flex-wrap">
                  <Link to={current.ctaLink} className="btn-gold" data-testid="hero-shop-btn">
                    {current.cta} <ArrowRight size={16} />
                  </Link>
                  <Link to="/shop" className="text-sm uppercase tracking-[0.2em] inline-flex items-center gap-1 hover:text-[#000] transition-colors link-underline">
                    Explore all <ArrowUpRight size={14} />
                  </Link>
                </div>

                {/* Trust strip in hero */}
                <div className="mt-12 flex items-center gap-8 flex-wrap">
                  <div className="flex -space-x-2">
                    {["FF7A45", "FED700", "333E48", "C4A77D"].map((c) => (
                      <span key={c} className="w-9 h-9 rounded-full ring-2 ring-[var(--aj-bg)]" style={{ backgroundColor: `#${c}` }} />
                    ))}
                  </div>
                  <div className="text-xs text-[#6A747D]">
                    <p className="text-[#333E48] font-semibold tracking-wide">★★★★★ 4.8 · 240+ reviews</p>
                    <p className="mt-0.5">Loved by Adire enthusiasts worldwide</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* IMAGE */}
          <div className="md:col-span-5 relative">
            <div className="relative">
              {/* Decorative offset frame */}
              <div className="absolute -top-3 -left-3 right-3 bottom-3 border border-[#333E48]/30 z-0" aria-hidden />

              <div className="aspect-[3/4] bg-[#F3EFEA] overflow-hidden shadow-[14px_14px_0px_0px_#333E48] relative z-10">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={slide + "-img"}
                    src={current.image}
                    alt={current.titleHighlight}
                    initial={{ opacity: 0, scale: 1.08 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="w-full h-full object-cover absolute inset-0"
                  />
                </AnimatePresence>

                {/* Floating "Shop the look" tag */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="absolute top-6 right-6 bg-white/95 backdrop-blur-md px-3 py-2 flex items-center gap-2 shadow-lg"
                >
                  <span className="w-2 h-2 rounded-full bg-[#FED700] animate-pulse" />
                  <span className="text-[10px] tracking-[0.25em] uppercase font-semibold">In Stock</span>
                </motion.div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={slide + "-badge"}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: 0.15, duration: 0.5 }}
                  className="absolute -bottom-6 -left-6 bg-[#000] text-white px-5 py-3 z-20 hidden md:block"
                  data-testid="hero-badge"
                >
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#FED700]">{current.badgeTitle}</p>
                  <p className="font-serif text-xl">{current.badgeSub}</p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* CAROUSEL CONTROLS */}
          <div className="absolute bottom-6 left-6 md:left-12 right-6 md:right-12 flex items-center justify-between z-20" data-testid="hero-controls">
            <div className="flex items-center gap-3" data-testid="hero-dots">
              {HERO_SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSlide(i)}
                  aria-label={`Slide ${i + 1}`}
                  data-testid={`hero-dot-${i}`}
                  className="relative h-[2px] w-12 bg-[#333E48]/15 overflow-hidden"
                >
                  <motion.span
                    className="absolute inset-y-0 left-0 bg-[#333E48]"
                    initial={{ width: "0%" }}
                    animate={{ width: i === slide ? "100%" : i < slide ? "100%" : "0%" }}
                    transition={i === slide && !paused ? { duration: 5.5, ease: "linear" } : { duration: 0.3 }}
                  />
                </button>
              ))}
              <span className="text-xs tracking-[0.25em] uppercase text-[#6A747D] ml-3 hidden sm:inline">
                {String(slide + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")}
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={goPrev} className="w-10 h-10 border border-[#333E48] hover:bg-[#000] hover:border-[#000] hover:text-white transition-colors flex items-center justify-center" aria-label="Previous" data-testid="hero-prev"><ChevronLeft size={16} /></button>
              <button onClick={goNext} className="w-10 h-10 border border-[#333E48] hover:bg-[#000] hover:border-[#000] hover:text-white transition-colors flex items-center justify-center" aria-label="Next" data-testid="hero-next"><ChevronRight size={16} /></button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#6A747D] z-10 pointer-events-none">
          <span className="text-[10px] tracking-[0.35em] uppercase">Scroll</span>
          <motion.span
            className="w-px h-8 bg-[#333E48]/40 origin-top"
            animate={{ scaleY: [0, 1, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </section>

      {/* PERKS STRIP */}
      <section className="border-y border-[#E8E2D9] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {PERKS.map(({ Icon, title, sub }) => (
            <div key={title} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FDFBF7] border border-[#E8E2D9] flex items-center justify-center">
                <Icon size={16} />
              </div>
              <div>
                <p className="text-sm font-medium">{title}</p>
                <p className="text-xs text-[#6A747D]">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORY TILES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="label-eyebrow mb-2">Shop by Category</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Find your edit</h2>
          </div>
          <Link to="/shop" className="text-sm uppercase tracking-[0.2em] inline-flex items-center gap-1 hover:text-[#FED700] transition-colors">
            All categories <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CATEGORY_TILES.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <Link to={c.link} className="group block relative overflow-hidden bg-[#F3EFEA] aspect-[4/5]" data-testid={`category-tile-${c.name}`}>
                <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white flex items-end justify-between">
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase opacity-80">{c.count}</p>
                    <h3 className="font-serif text-3xl mt-1">{c.name}</h3>
                  </div>
                  <div className="w-10 h-10 bg-[#FED700] text-[#333E48] flex items-center justify-center translate-y-0 group-hover:-translate-y-1 transition-transform">
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRODUCT TABS */}
      <section className="bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="label-eyebrow mb-2">The Collection</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Curated for you</h2>
            </div>
            <div className="flex gap-2" data-testid="home-tabs">
              {tabs.map((t) => (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  data-testid={`home-tab-${t.k}`}
                  className={`px-5 py-2.5 text-xs tracking-[0.2em] uppercase border transition-all ${
                    tab === t.k ? "bg-[#333E48] text-white border-[#333E48]" : "bg-transparent text-[#333E48] border-[#E8E2D9] hover:border-[#333E48]"
                  }`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-12"
              data-testid="home-product-grid"
            >
              {visible.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </motion.div>
          </AnimatePresence>

          <div className="text-center mt-16">
            <Link to="/shop" className="btn-outline" data-testid="home-view-all-btn">View All Products</Link>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#FED700] text-[#333E48] py-6 overflow-hidden border-y border-[#333E48]">
        <div className="flex gap-12 whitespace-nowrap animate-[scroll_28s_linear_infinite]">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex gap-12 items-center">
              {["Adire Heritage", "Hand-dyed", "Made in Lagos", "Premium Quality", "Shop the Collection", "Authentic Yoruba Craft"].map((t) => (
                <span key={t + k} className="font-serif text-3xl tracking-tight flex items-center gap-12">
                  ✦ {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* EDITORIAL */}
      <section className="bg-[#333E48] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="label-eyebrow !text-[#FED700] mb-6">Our Craft</p>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tighter leading-[1.02]">
              Adire dipped.<br />
              <em className="italic" style={{ background: "linear-gradient(transparent 60%, #FED70033 60%)" }}>Patiently</em> made.
            </h2>
            <p className="mt-8 text-[#C4C9CD] leading-relaxed max-w-md">
              Every Ajoyo piece celebrates the centuries-old Yoruba tradition of Adire resist-dyed textile artistry from southwest Nigeria. We pair this heritage with modern silhouettes for a wardrobe that travels with you.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[["120+", "Patterns"], ["100%", "Quality"], ["100%", "Handmade"]].map(([n, l]) => (
                <div key={l}>
                  <p className="font-serif text-4xl text-[#FED700]">{n}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C4C9CD] mt-1">{l}</p>
                </div>
              ))}
            </div>
            <Link to="/shop" className="inline-flex items-center gap-2 mt-10 text-sm uppercase tracking-[0.25em] border-b border-[#FED700] pb-1 hover:gap-3 transition-all">
              Explore the collection <ArrowRight size={16} />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-2 gap-4"
          >
            <div className="aspect-[4/5] overflow-hidden bg-[#222]">
              <img src="https://store.ajoyo.org/wp-content/uploads/2026/05/83f6da75-7957-4713-bd6f-34b05705685e.jpeg" alt="craft" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[4/5] overflow-hidden bg-[#222] mt-12">
              <img src="https://store.ajoyo.org/wp-content/uploads/2026/02/000A6655.jpg-scaled.jpeg" alt="craft" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
          <p className="label-eyebrow mb-4">Newsletter</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[1.05]">
            Be first to know.
          </h2>
          <p className="mt-6 text-[#6A747D] max-w-md mx-auto">
            New drops, restocks, and stories from the Ajoyo studio straight to your inbox.
          </p>
          <form
            className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            onSubmit={(e) => { e.preventDefault(); }}
            data-testid="newsletter-form"
          >
            <input
              type="email"
              required
              placeholder="Your email address"
              className="aj-input flex-1"
              data-testid="newsletter-email"
            />
            <button className="btn-gold" data-testid="newsletter-submit">Subscribe</button>
          </form>
          <p className="mt-6 text-xs text-[#6A747D] uppercase tracking-[0.2em]">No spam. Unsubscribe anytime.</p>
        </div>
      </section>
    </div>
  );
}
